/**
 * Nai Data Privacy Assessment — Public Form
 *
 * 2 etapas (após consent universal):
 *   1. Perguntas (5 blocos: Governança, Data Map, DSAR, Proteção, Incidentes)
 *   2. Dados cadastrais (CNPJ auto-lookup + BANT)
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  ChevronLeft, ChevronRight, CheckCircle2, Building2, ShieldAlert,
  Database, FileSearch, Users as UsersIcon, Lock, AlertTriangle,
} from 'lucide-react'
import AssessmentConsentStep, { ConsentValues } from '../components/AssessmentConsentStep'
import { GlossaryProvider, renderWithGlossary } from '../components/assessment/glossary'

const STORAGE_KEY = 'privacy_draft_v1'
const API = '/api/v1/assessments/privacy-maturity/submit'

type DadosCadastrais = {
  razao_social: string
  cnpj: string
  inscricao_estadual: string
  endereco: string
  sponsor_nome: string
  sponsor_email: string
  telefone_fixo: string
  celular: string
  cargo: string
  cargo_outro: string
  setor: string
  porte: string
  urgencia: string
}

const CARGO_OPTS = ['C-Level', 'DPO / Encarregado', 'Diretor', 'Gerente Compliance', 'Gerente TI', 'Coordenador', 'Analista', 'Outro']
const SETOR_OPTS = [
  'Financeiro / Banking', 'Saúde', 'Energia / Utilities', 'Telecom', 'Governo',
  'Agronegócio', 'Indústria', 'Manufatura', 'Varejo', 'Logística',
  'Tecnologia', 'Educação', 'Construção', 'Outro',
]
const PORTE_OPTS = ['Até 100', '101 — 500', '501 — 2.000', '2.001 — 10.000', 'Mais de 10.000']
const URGENCIA_OPTS = ['Agora', '3 meses', '6 meses', '12 meses', 'Apenas avaliando']

const BLOCKED_EMAIL_DOMAINS = new Set([
  'gmail.com', 'hotmail.com', 'yahoo.com', 'yahoo.com.br',
  'outlook.com', 'live.com', 'icloud.com', 'msn.com',
  'bol.com.br', 'uol.com.br', 'terra.com.br', 'globo.com',
  'aol.com', 'mail.com', 'protonmail.com',
])

type QType = 'single' | 'multi'
type Question = {
  id: string; label: string; type: QType; options: string[]; allowOther?: boolean
}
type Group = { id: string; title: string; subtitle: string; icon: any; questions: Question[] }

// Exportado: o painel admin de Assessments usa os mesmos rótulos para exibir as respostas
export const QUESTION_GROUPS: Group[] = [
  {
    id: 'governanca', title: 'Governança & Regulatório', icon: ShieldAlert,
    subtitle: 'DPO, ROPA, políticas, base legal e treinamento',
    questions: [
      { id: 'q_dpo', label: 'Sua empresa tem DPO (Encarregado de Dados)?', type: 'single',
        options: ['Sim — interno dedicado', 'Sim — interno compartilhado com Compliance/Jurídico',
                  'Sim — terceirizado (DPO as a Service)', 'Não — em planejamento', 'Não'] },
      { id: 'q_regulamentos_escopo', label: 'Quais regulamentações você precisa atender?', type: 'multi',
        options: ['LGPD', 'GDPR', 'ISO 27701', 'ISO 27001', 'HIPAA', 'PCI-DSS', 'SOX', 'CCPA', 'BACEN', 'Nenhuma', 'Não sei'] },
      { id: 'q_ropa', label: 'Possui ROPA (Registro de Atividades de Tratamento)?', type: 'single',
        options: ['Sim — completo e atualizado', 'Sim — parcial', 'Em projeto', 'Não'] },
      { id: 'q_politicas', label: 'Possui políticas formais de privacidade?', type: 'single',
        options: ['Sim — públicas e internas atualizadas', 'Sim — somente públicas', 'Sim — desatualizadas', 'Não'] },
      { id: 'q_treinamento_lgpd', label: 'Programa de treinamento LGPD para colaboradores?', type: 'single',
        options: ['Contínuo + reciclagem anual', 'Anual', 'Onboarding apenas', 'Ad-hoc', 'Não tem'] },
    ],
  },
  {
    id: 'data_map', title: 'Inventário & Data Map', icon: Database,
    subtitle: 'Classificação, dados sensíveis, fluxos e ciclo de vida',
    questions: [
      { id: 'q_dados_classificados', label: 'Os dados pessoais foram classificados/inventariados?', type: 'single',
        options: ['Sim — automatizado (ferramenta de Data Discovery)', 'Sim — manual em planilha', 'Parcial — apenas dados críticos', 'Não'] },
      { id: 'q_dados_sensiveis', label: 'Tratam dados sensíveis (art. 11 LGPD: saúde, biometria, religião, raça)?', type: 'single',
        options: ['Sim — alto volume', 'Sim — pequeno volume', 'Não', 'Não sei'] },
      { id: 'q_data_map', label: 'Possui mapa de fluxos de dados (Data Flow Map)?', type: 'single',
        options: ['Sim — automatizado (atualização contínua)', 'Sim — em planilha', 'Em projeto', 'Não'] },
      { id: 'q_ciclo_vida', label: 'Gestão do ciclo de vida de dados (retenção/expurgo)?', type: 'single',
        options: ['Automatizada com expurgo programado', 'Procedural manual', 'Ad-hoc', 'Não definida'] },
      { id: 'q_dados_transferencia', label: 'Há transferência internacional de dados pessoais?', type: 'single',
        options: ['Sim — países com decisão de adequação ANPD', 'Sim — sem adequação (precisa cláusulas contratuais)', 'Não', 'Não sei'] },
    ],
  },
  {
    id: 'titular', title: 'Direitos do Titular & DSAR', icon: UsersIcon,
    subtitle: 'Canal, SLA e operacionalização das requisições',
    questions: [
      { id: 'q_canal_titular', label: 'Canal para direitos do titular (DSAR)?', type: 'single',
        options: ['Self-service automatizado', 'Formulário web', 'E-mail dedicado', 'E-mail genérico', 'Não tem'] },
      { id: 'q_sla_dsar', label: 'SLA de resposta a DSAR (LGPD: 15 dias)?', type: 'single',
        options: ['< 5 dias', '5 — 15 dias', '15 — 30 dias', 'Sem SLA definido', 'Sem processo'] },
      { id: 'q_dsar_volume', label: 'Volume médio mensal de solicitações de titulares?', type: 'single',
        options: ['0', '1 — 10', '11 — 50', '51 — 200', 'Mais de 200', 'Não sei'] },
    ],
  },
  {
    id: 'protecao', title: 'Proteção & Compartilhamento', icon: Lock,
    subtitle: 'Criptografia, mascaramento, anonimização e Authoritative Repository',
    questions: [
      { id: 'q_compartilhamento', label: 'Compartilhamento de dados pessoais com terceiros?', type: 'single',
        options: ['Frequente (parceiros, fornecedores, APIs)', 'Eventual', 'Raro', 'Não compartilha'] },
      { id: 'q_criptografia_repouso', label: 'Criptografia em repouso (banco de dados, arquivos)?', type: 'single',
        options: ['Em 100% dos dados pessoais', 'Em sensíveis apenas', 'Parcial', 'Não'] },
      { id: 'q_criptografia_transito', label: 'Criptografia em trânsito (TLS, VPN)?', type: 'single',
        options: ['100% (TLS interno e externo)', 'Parcial', 'Apenas em externos', 'Não'] },
      { id: 'q_mascaramento', label: 'Mascaramento/anonimização para uso não-produtivo (DEV, QA, BI)?', type: 'single',
        options: ['Sim — automatizado em DEV/QA/BI', 'Sim — manual sob demanda', 'Não', 'Não sei'] },
      { id: 'q_authoritative_repo', label: 'Possui Authoritative Repository com token de acesso para compartilhamento seguro?', type: 'single',
        options: ['Sim — implementado em produção', 'Em projeto', 'Não tem', 'Não sei o que é'] },
      { id: 'q_tokenizacao', label: 'Tokenização para PII/PCI sensível?', type: 'single',
        options: ['Sim — em produção (PII/PCI)', 'Em projeto', 'Não', 'Não sei'] },
    ],
  },
  {
    id: 'incidentes', title: 'Incidentes & Conformidade', icon: AlertTriangle,
    subtitle: 'Resposta a incidentes, notificação ANPD e auditoria',
    questions: [
      { id: 'q_processo_incidente', label: 'Processo de resposta a incidente LGPD?', type: 'single',
        options: ['Documentado e testado (tabletop anual)', 'Documentado', 'Informal', 'Não tem'] },
      { id: 'q_notificacao_anpd', label: 'Capaz de notificar ANPD em < 72h após incidente?', type: 'single',
        options: ['Sim, processo definido (< 72h)', 'Talvez', 'Não'] },
      { id: 'q_auditoria_privacidade', label: 'Auditoria de privacidade externa?', type: 'single',
        options: ['Anual com auditor externo', 'Eventual', 'Não tem'] },
      { id: 'q_assessment_terceiros', label: 'Avalia maturidade LGPD dos fornecedores?', type: 'single',
        options: ['Sim — formal por contrato', 'Sim — informal', 'Não'] },
      { id: 'q_ferramentas_atuais', label: 'Ferramentas de privacidade já em uso?', type: 'multi',
        options: ['OneTrust', 'TrustArc', 'Microsoft Purview', 'Securiti', 'BigID', 'Varonis', 'Próprio', 'Nai Data Privacy', 'Nai Data Discovery', 'Nenhuma', 'Outro'],
        allowOther: true },
    ],
  },
]

const STEPS = [
  { id: 1, label: 'Perguntas', icon: FileSearch },
  { id: 2, label: 'Dados',     icon: Building2 },
]

function formatCNPJ(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 14)
  if (d.length <= 2) return d
  if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`
  if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`
}

export default function AssessmentPrivacyPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [consentAccepted, setConsentAccepted] = useState(false)
  const [consents, setConsents] = useState<ConsentValues>({ relatorio: false, contato: false, newsletter: false })
  const [dados, setDados] = useState<DadosCadastrais>({
    razao_social: '', cnpj: '', inscricao_estadual: '', endereco: '',
    sponsor_nome: '', sponsor_email: '', telefone_fixo: '', celular: '',
    cargo: '', cargo_outro: '', setor: '', porte: '', urgencia: '',
  })
  const [informacoes, setInformacoes] = useState<Record<string, any>>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const [cnpjLoading, setCnpjLoading] = useState(false)
  const [cnpjMessage, setCnpjMessage] = useState<{ text: string; ok: boolean } | null>(null)
  const lastLookedCnpj = useRef<string>('')

  async function handleCnpjLookup(rawCnpj: string) {
    const raw = rawCnpj.replace(/\D/g, '')
    if (raw.length !== 14) return
    if (lastLookedCnpj.current === raw) return
    lastLookedCnpj.current = raw
    setCnpjLoading(true); setCnpjMessage(null)
    try {
      const { data } = await axios.post('/api/v1/assessments/lookup-cnpj', { cnpj: raw })
      if (!data.found) {
        setCnpjMessage({ text: 'CNPJ não localizado na Receita Federal', ok: false })
        return
      }
      setDados(prev => ({
        ...prev,
        cnpj: data.cnpj || prev.cnpj,
        razao_social: prev.razao_social || data.razao_social || data.nome_fantasia || '',
        endereco: prev.endereco || data.endereco_formatado || '',
        telefone_fixo: prev.telefone_fixo || data.telefone || '',
      }))
      setCnpjMessage({ text: `Dados de "${data.razao_social || data.nome_fantasia}" carregados`, ok: true })
    } catch (err: any) {
      setCnpjMessage({ text: err?.response?.data?.detail || 'Falha na consulta', ok: false })
    } finally {
      setCnpjLoading(false)
    }
  }

  // Draft restore
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const d = JSON.parse(raw)
        if (d.step) setStep(d.step)
        if (d.dados) setDados(d.dados)
        if (d.informacoes) setInformacoes(d.informacoes)
        if (d.consents) { setConsents(d.consents); if (d.consents.relatorio) setConsentAccepted(true) }
      }
    } catch {/* ignore */}
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, dados, informacoes, consents }))
    }, 600)
    return () => clearTimeout(t)
  }, [step, dados, informacoes, consents])

  function validateStep(): { errors: Record<string, string>; firstFieldId: string | null } {
    const errors: Record<string, string> = {}
    let firstFieldId: string | null = null
    if (step === 2) {
      if (!dados.razao_social.trim()) { errors['razao_social'] = 'Obrigatório'; firstFieldId = firstFieldId || 'razao_social' }
      if (!dados.cnpj.trim() || dados.cnpj.replace(/\D/g, '').length !== 14) {
        errors['cnpj'] = 'CNPJ deve ter 14 dígitos'; firstFieldId = firstFieldId || 'cnpj'
      }
      if (!dados.sponsor_nome.trim()) { errors['sponsor_nome'] = 'Obrigatório'; firstFieldId = firstFieldId || 'sponsor_nome' }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dados.sponsor_email)) {
        errors['sponsor_email'] = 'E-mail inválido'; firstFieldId = firstFieldId || 'sponsor_email'
      } else {
        const d = dados.sponsor_email.toLowerCase().split('@')[1]
        if (BLOCKED_EMAIL_DOMAINS.has(d)) {
          errors['sponsor_email'] = `Use seu e-mail corporativo (${d} não é aceito)`
          firstFieldId = firstFieldId || 'sponsor_email'
        }
      }
      if (!dados.cargo) { errors['cargo'] = 'Obrigatório'; firstFieldId = firstFieldId || 'cargo' }
      else if (dados.cargo === 'Outro' && !dados.cargo_outro.trim()) {
        errors['cargo'] = 'Especifique seu cargo'; firstFieldId = firstFieldId || 'cargo'
      }
      if (!dados.setor) { errors['setor'] = 'Obrigatório'; firstFieldId = firstFieldId || 'setor' }
      if (!dados.porte) { errors['porte'] = 'Obrigatório'; firstFieldId = firstFieldId || 'porte' }
      if (!dados.urgencia) { errors['urgencia'] = 'Obrigatório'; firstFieldId = firstFieldId || 'urgencia' }
    }
    return { errors, firstFieldId }
  }

  function focusField(fid: string) {
    setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-field-id="${fid}"]`)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const input = el.querySelector<HTMLElement>('input, select, textarea')
      if (input) input.focus({ preventScroll: true })
    }, 50)
  }

  function handleNext() {
    const { errors, firstFieldId } = validateStep()
    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      if (firstFieldId) focusField(firstFieldId); return
    }
    setValidationErrors({})
    setStep(s => Math.min(STEPS.length, s + 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function handleSubmit() {
    const { errors, firstFieldId } = validateStep()
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors); if (firstFieldId) focusField(firstFieldId); return
    }
    setSubmitting(true); setError(null)
    try {
      const dadosFinal = (dados.cargo === 'Outro' && dados.cargo_outro.trim())
        ? { ...dados, cargo: dados.cargo_outro.trim() } : dados
      const { data } = await axios.post(API, {
        dados_cadastrais: dadosFinal,
        informacoes,
        consents,
      })
      localStorage.removeItem(STORAGE_KEY)
      navigate(`/assessment/privacy/resultado/${data.submission_id}`)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao enviar. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!consentAccepted) {
    return (
      <AssessmentConsentStep partner="NAI"
        onAccept={(c) => { setConsents(c); setConsentAccepted(true) }}
        initial={consents} />
    )
  }

  return (
    <GlossaryProvider>
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex items-center justify-center gap-4">
            <img src="https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png"
              alt="NAI" style={{ height: '56px', width: 'auto' }} />
            <span className="text-2xl font-light text-slate-400">·</span>
            <span className="text-sm font-bold uppercase tracking-wider text-cyan-300">Data Privacy</span>
          </div>
          <p className="text-center text-sm text-slate-300 mt-3 italic">
            Diagnóstico multi-regulatório · LGPD · GDPR · ISO 27701
          </p>
          <p className="text-center text-[10px] uppercase tracking-widest text-cyan-400/70 mt-2">
            Assessment Privacidade · ~15 min
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Stepper */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-xs">
            {STEPS.map((s, i) => {
              const SIcon = s.icon
              const active = step === s.id
              const stepDone = step > s.id
              return (
                <li key={s.id} className="flex items-center gap-2 flex-1">
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold ${
                    active ? 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-200'
                    : stepDone ? 'bg-emerald-500/10 border border-emerald-500/40 text-emerald-200'
                    : 'bg-slate-800/60 border border-slate-700 text-slate-500'
                  }`}>
                    <SIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className="h-px flex-1 bg-slate-700" />}
                </li>
              )
            })}
          </ol>
        </nav>

        {Object.keys(validationErrors).length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-red-900/30 border border-red-700/60 text-red-200 text-sm flex items-start gap-3">
            <span className="text-xl leading-none">⚠️</span>
            <div>
              <div className="font-bold text-red-100">Faltam campos obrigatórios</div>
              <div className="text-red-300/90 text-xs">Os campos destacados em vermelho precisam ser preenchidos.</div>
            </div>
          </div>
        )}

        {step === 1 && (
          <StepPerguntas values={informacoes} onChange={setInformacoes} />
        )}
        {step === 2 && (
          <StepDados dados={dados} onChange={setDados} errors={validationErrors}
            cnpjLoading={cnpjLoading} cnpjMessage={cnpjMessage}
            onCnpjChange={(formatted) => {
              setDados(prev => ({ ...prev, cnpj: formatted }))
              const raw = formatted.replace(/\D/g, '')
              if (raw.length === 14) handleCnpjLookup(raw)
              else if (raw.length < 14) { setCnpjMessage(null); lastLookedCnpj.current = '' }
            }}
          />
        )}

        {error && (
          <div className="mt-4 p-3 rounded-md bg-red-900/30 border border-red-700/60 text-red-200 text-sm">{error}</div>
        )}

        <div className="mt-10 flex items-center justify-between gap-3">
          <button type="button"
            onClick={() => { setValidationErrors({}); setStep(s => Math.max(1, s - 1)); window.scrollTo({top:0, behavior:'smooth'}) }}
            disabled={step === 1}
            className="px-5 py-2.5 rounded-full border border-slate-700 text-slate-300 hover:bg-slate-800/60 disabled:opacity-30 transition text-sm font-semibold inline-flex items-center gap-1.5">
            <ChevronLeft className="h-4 w-4" /> Voltar
          </button>
          <div className="text-xs text-slate-500">Etapa {step}/{STEPS.length}</div>
          {step < STEPS.length ? (
            <button type="button" onClick={handleNext}
              className="px-6 py-2.5 rounded-full bg-gradient-to-r from-cyan-600 to-emerald-600 text-white font-bold text-sm shadow-lg hover:shadow-xl hover:scale-[1.02] transition inline-flex items-center gap-1.5">
              Continuar <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={handleSubmit} disabled={submitting}
              className="px-6 py-2.5 rounded-full bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold text-sm shadow-lg disabled:opacity-50">
              {submitting ? 'Enviando…' : 'Concluir e ver resultado'}
            </button>
          )}
        </div>
      </main>
    </div>
    </GlossaryProvider>
  )
}

// ─── Step 1: Perguntas ──────────────────────────────────────────────────────

function StepPerguntas({ values, onChange }: {
  values: Record<string, any>
  onChange: (v: Record<string, any>) => void
}) {
  const update = (k: string, v: any) => onChange({ ...values, [k]: v })
  const total = QUESTION_GROUPS.reduce((s, g) => s + g.questions.length, 0)
  const answered = QUESTION_GROUPS.reduce((s, g) => s + g.questions.filter(q => {
    const v = values[q.id]
    return q.type === 'multi' ? Array.isArray(v) && v.length > 0 : v && String(v).trim()
  }).length, 0)

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <FileSearch className="h-6 w-6 text-cyan-400" /> Diagnóstico de Privacidade
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          Avaliação alinhada à tríade Nai: <strong>Data Privacy</strong> + <strong>Data Discovery</strong> + <strong>Authoritative Repository</strong>.
        </p>
        <div className="mt-3 text-xs text-slate-500">
          <strong className="text-cyan-300">{answered}</strong> de {total} perguntas respondidas
        </div>
      </div>

      {QUESTION_GROUPS.map((group) => {
        const GIcon = group.icon
        const blockAnswered = group.questions.filter(q => {
          const v = values[q.id]
          return q.type === 'multi' ? Array.isArray(v) && v.length > 0 : v && String(v).trim()
        }).length
        return (
          <section key={group.id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <GIcon className="h-5 w-5 text-cyan-400" />
                <h3 className="text-lg font-bold text-white">{group.title}</h3>
              </div>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                {blockAnswered}/{group.questions.length}
              </span>
            </div>
            <p className="text-xs text-slate-400 mb-5">{group.subtitle}</p>
            <div className="space-y-5">
              {group.questions.map((q) => (
                <QuestionField key={q.id} q={q} value={values[q.id]}
                  otherValue={values[`${q.id}_other`] || ''}
                  onChange={(v) => update(q.id, v)}
                  onOtherChange={(v) => update(`${q.id}_other`, v)} />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

function QuestionField({ q, value, otherValue, onChange, onOtherChange }: {
  q: Question; value: any; otherValue: string
  onChange: (v: any) => void; onOtherChange: (v: string) => void
}) {
  const isOtherToken = (s: string) => /^outro$/i.test((s || '').trim())

  if (q.type === 'single') {
    const sel = value || ''
    const isOtherSelected = isOtherToken(sel)
    return (
      <div data-field-id={q.id}>
        <label className="block text-sm font-semibold text-slate-200 mb-2">{renderWithGlossary(q.label)}</label>
        <div className="flex flex-wrap gap-2">
          {q.options.map((o) => {
            const active = sel === o
            return (
              <button key={o} type="button"
                onClick={() => { onChange(o); if (!isOtherToken(o) && otherValue) onOtherChange('') }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  active ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                  : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                }`}>{o}</button>
            )
          })}
        </div>
        {isOtherSelected && q.allowOther && <OtherInput value={otherValue} onChange={onOtherChange} />}
      </div>
    )
  }
  const sel: string[] = Array.isArray(value) ? value : []
  const isOtherSelected = sel.some(isOtherToken)
  return (
    <div data-field-id={q.id}>
      <label className="block text-sm font-semibold text-slate-200 mb-2">{renderWithGlossary(q.label)}</label>
      <div className="flex flex-wrap gap-2">
        {q.options.map((o) => {
          const active = sel.includes(o)
          return (
            <button key={o} type="button"
              onClick={() => {
                const next = active ? sel.filter(x => x !== o) : [...sel, o]
                onChange(next)
                if (active && isOtherToken(o) && otherValue) onOtherChange('')
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                active ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
              }`}>{o}</button>
          )
        })}
      </div>
      {isOtherSelected && q.allowOther && <OtherInput value={otherValue} onChange={onOtherChange} />}
    </div>
  )
}

function OtherInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="mt-3 pl-3 border-l-2 border-cyan-500/50">
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-cyan-300 mb-1.5">Descreva o "Outro"</label>
      <input type="text" value={value} placeholder="Especifique aqui…"
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-cyan-700/60 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm placeholder-slate-500" />
    </div>
  )
}

// ─── Step 2: Dados cadastrais ───────────────────────────────────────────────

function StepDados({ dados, onChange, errors, cnpjLoading, cnpjMessage, onCnpjChange }: {
  dados: DadosCadastrais
  onChange: (d: DadosCadastrais) => void
  errors: Record<string, string>
  cnpjLoading: boolean
  cnpjMessage: { text: string; ok: boolean } | null
  onCnpjChange: (formatted: string) => void
}) {
  const set = (k: keyof DadosCadastrais) => (v: string) => onChange({ ...dados, [k]: v })
  return (
    <section className="space-y-4">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Building2 className="h-6 w-6 text-cyan-400" /> Dados cadastrais
      </h2>
      <p className="text-sm text-slate-400">
        Comece pelo CNPJ — a Razão Social, endereço e telefone serão preenchidos automaticamente.
      </p>

      <div data-field-id="cnpj">
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">
          CNPJ <span className="text-red-400 ml-1">*</span>
        </label>
        <div className="relative">
          <input type="text" value={dados.cnpj} placeholder="00.000.000/0000-00"
            inputMode="numeric" maxLength={18}
            onChange={(e) => onCnpjChange(formatCNPJ(e.target.value))}
            className={`w-full px-3 py-2.5 pr-10 rounded-lg bg-slate-800 border text-slate-100 focus:outline-none focus:ring-2 text-sm font-mono ${
              errors['cnpj'] ? 'border-red-500/70 focus:ring-red-500' : 'border-slate-700 focus:ring-cyan-500'
            }`} />
          {cnpjLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="h-4 w-4 rounded-full border-2 border-cyan-500/30 border-t-cyan-400 animate-spin"></div>
            </div>
          )}
          {!cnpjLoading && cnpjMessage?.ok && (
            <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-400" />
          )}
        </div>
        {errors['cnpj'] && <p className="mt-1.5 text-xs text-red-400 font-semibold">{errors['cnpj']}</p>}
        {cnpjMessage && (
          <p className={`mt-1.5 text-xs font-semibold ${cnpjMessage.ok ? 'text-emerald-400' : 'text-amber-400'}`}>
            {cnpjMessage.ok ? '✓ ' : ''}{cnpjMessage.text}
          </p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Field label="Razão Social *" required value={dados.razao_social} onChange={set('razao_social')}
          fieldId="razao_social" error={errors['razao_social']} />
        <Field label="Inscrição Estadual" value={dados.inscricao_estadual} onChange={set('inscricao_estadual')}
          placeholder="000.000.000.000 ou ISENTO" />
        <div className="sm:col-span-2">
          <Field label="Endereço" value={dados.endereco} onChange={set('endereco')} />
        </div>
        <Field label="Nome completo (Sponsor) *" required value={dados.sponsor_nome} onChange={set('sponsor_nome')}
          fieldId="sponsor_nome" error={errors['sponsor_nome']} />
        <Field label="E-mail corporativo *" required type="email" value={dados.sponsor_email}
          onChange={set('sponsor_email')} fieldId="sponsor_email" error={errors['sponsor_email']} />
        <Field label="Telefone fixo" value={dados.telefone_fixo} onChange={set('telefone_fixo')} />
        <Field label="Celular / WhatsApp" value={dados.celular} onChange={set('celular')} placeholder="+55 11 99999-9999" />
      </div>

      <div className="mt-6 pt-6 border-t border-slate-800">
        <h3 className="text-lg font-bold text-white mb-1">Qualificação BANT</h3>
        <p className="text-xs text-slate-400 mb-4">Informações que ajudam a Nai a calibrar a proposta.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <PillSelect label="Qual o seu cargo? *" value={dados.cargo} onChange={set('cargo')}
            options={CARGO_OPTS} fieldId="cargo" error={errors['cargo']}
            otherValue={dados.cargo_outro} onOtherChange={set('cargo_outro')}
            otherPlaceholder="Ex.: CFO · COO · DPO · Head de Compliance…" />
          <PillSelect label="Setor *" value={dados.setor} onChange={set('setor')}
            options={SETOR_OPTS} fieldId="setor" error={errors['setor']} />
          <PillSelect label="Porte (colaboradores) *" value={dados.porte} onChange={set('porte')}
            options={PORTE_OPTS} fieldId="porte" error={errors['porte']} />
          <PillSelect label="Horizonte para implantar Data Privacy *" value={dados.urgencia} onChange={set('urgencia')}
            options={URGENCIA_OPTS} fieldId="urgencia" error={errors['urgencia']} />
        </div>
      </div>
    </section>
  )
}

function Field({ label, value, onChange, required, type = 'text', placeholder, fieldId, error }: {
  label: string; value: string; onChange: (v: string) => void; required?: boolean
  type?: string; placeholder?: string; fieldId?: string; error?: string
}) {
  return (
    <div data-field-id={fieldId}>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input type={type} value={value} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className={`w-full px-3 py-2.5 rounded-lg bg-slate-800 border text-slate-100 focus:outline-none focus:ring-2 text-sm ${
          error ? 'border-red-500/70 focus:ring-red-500' : 'border-slate-700 focus:ring-cyan-500'
        }`} />
      {error && <p className="mt-1.5 text-xs text-red-400 font-semibold">{error}</p>}
    </div>
  )
}

function PillSelect({ label, value, onChange, options, fieldId, error,
                     otherValue, onOtherChange, otherPlaceholder }: {
  label: string; value: string; onChange: (v: string) => void; options: string[]
  fieldId?: string; error?: string
  otherValue?: string; onOtherChange?: (v: string) => void; otherPlaceholder?: string
}) {
  const isOther = /^outro$/i.test(value || '')
  return (
    <div data-field-id={fieldId}>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {options.map(o => {
          const active = value === o
          return (
            <button key={o} type="button"
              onClick={() => {
                onChange(o)
                if (!/^outro$/i.test(o) && onOtherChange && otherValue) onOtherChange('')
              }}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                active ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                : error ? 'bg-slate-800/60 border-red-500/40 text-slate-400 hover:border-red-400'
                : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
              }`}>{o}</button>
          )
        })}
      </div>
      {isOther && onOtherChange && (
        <div className="mt-2 pl-3 border-l-2 border-cyan-500/50">
          <input type="text" value={otherValue || ''} placeholder={otherPlaceholder || 'Especifique aqui…'}
            onChange={(e) => onOtherChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-cyan-700/60 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm placeholder-slate-500" />
        </div>
      )}
      {error && <p className="mt-1.5 text-xs text-red-400 font-semibold">{error}</p>}
    </div>
  )
}
